import React, { useState, useEffect, useCallback } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Material
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Tooltip from '@material-ui/core/Tooltip';

// Material Icons
import FilterListIcon from '@material-ui/icons/FilterList';
import DateRangeIcon from '@material-ui/icons/DateRange';
import DirectionsBusIcon from '@material-ui/icons/DirectionsBus';
import PersonPinIcon from '@material-ui/icons/PersonPin';
import PaymentIcon from '@material-ui/icons/Payment';
import HomeWorkIcon from '@material-ui/icons/HomeWork';
import TodayIcon from '@material-ui/icons/Today';
import DriveEtaIcon from '@material-ui/icons/DriveEta';
import SupervisorAccount from '@material-ui/icons/SupervisorAccount';
import AdjustIcon from '@material-ui/icons/Adjust';
import BeenhereTwoToneIcon from '@material-ui/icons/BeenhereTwoTone';
import ClassTwoTone from '@material-ui/icons/ClassTwoTone';
import DoneAllTwoToneIcon from '@material-ui/icons/DoneAllTwoTone';
import Commute from '@material-ui/icons/Commute';
import Autorenew from '@material-ui/icons/Autorenew';
import Receipt from '@material-ui/icons/Receipt';
import PersonIcon from '@material-ui/icons/Person';
import VoucherIcon from '@material-ui/icons/MarkunreadMailbox';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import WalletIcon from '@material-ui/icons/AccountBalanceWalletOutlined';

// MDI
import Icon from '@mdi/react';
import { mdiQrcode, mdiBusMarker } from '@mdi/js';

// Actions
import {
  setSelectedTokenStatus,
  setSelectedCreditType,
  setSelectedDbds,
  setSelectedSupervisors,
  setSelectedEmployees,
  setSelectedLines,
  setSelectedProfiles,
  setSelectedPayments,
  setSelectedReceivePayments,
  setSelectedSellingPoint,
  setSelectedSellingTerminal,
  setSelectedStartDate,
  setSelectedEndDate,
  setSelectedStartedAt,
  setSelectedQRCodes,
  setSelectedQuantity,
} from 'store/ducks/reports/actions';
import { setOpenModal } from 'store/ducks/modalFilter/actions';
import {
  setSelectedScore,
  setSelectedStatus,
  setSelectedApprovalStatus,
  setSelectedValidationStatus,
} from 'store/ducks/facial/actions';
import { setSelectedStartDate as dateSelectedStartDate } from 'store/ducks/dates/actions';
import {
  setSelectedType,
  setSelectedStatus as companySelectedStatus,
  setSelectedPaymentType,
} from 'store/ducks/companies/actions';
import { setSelectedReceivingType } from 'store/ducks/sellingTerminals/actions';
import { setSelectedSearchUser } from 'store/ducks/users/actions';
import { setToggleVoucher } from 'store/ducks/voucher/actions';

// Types
import { ApplicationState } from 'store';

// Components
import { SelectWeekDay } from 'components';

import {
  CarsFilter,
  PeriodFilter,
  LineFilter,
  ProfileFilter,
  PaymentFilter,
  ReceiveFilter,
  SupervisorFilter,
  DbdFilter,
  ScoreFilter,
  StatusFilter,
  ApprovalStatusFilter,
  TokenStatusFilter,
  KmTypeFilter,
  SellingPointFilter,
  SellingTerminalFilter,
  ValidationStatusFilter,
  CreditTypeFilter,
  ReceivingTypeFilter,
  SeccionedLinesFilter,
  AmountOfDaysFilter,
  UserFilter,
  DateTypeFilter,
  VoucherFilter,
  BalanceTypeFilter,
  FilenameFilter,
  CollectionFilter,
} from './components/Filters';
import {
  TypeFilter,
  StatusFilter as TransportVoucherStatusFilter,
  PaymentTypeFilter,
} from './components/Filters/TransportVoucherCollection';

import { QRCodeFilter } from './components/Filters/QRCodeFilter';
import { UsageFilter } from './components/Filters/UsageFilter';
import { HistoryFilter } from './components/Filters/HistoryFilter';

import { DriverNavigation } from '../../pages/HalfTripDiary/components/HalfTripDiaryDatails/components';

// Styles
import useStyles from './chartFilterModal.styles';

type FilterTypes =
  | 'DAY'
  | 'PERIOD'
  | 'USAGE'
  | 'PAYMENT'
  | 'QRCODE_TYPE'
  | 'LINE_TYPE'
  | 'PROFILE'
  | 'LINE'
  | 'DRIVER'
  | 'CAR'
  | 'RECEIVE_PAYMENT'
  | 'SUPERVISOR'
  | 'DBD'
  | 'SCORE'
  | 'STATUS'
  | 'APPROVAL_STATUS'
  | 'TOKEN_STATUS'
  | 'KM_TYPE'
  | 'TRANSPORT_VOUCHER_TYPE'
  | 'TRANSPORT_VOUCHER_STATUS'
  | 'TRANSPORT_VOUCHER_PAYMENT_TYPE'
  | 'SELLING_POINT'
  | 'SELLING_TERMINAL'
  | 'REGISTRATION_VALIDATION_STATUS'
  | 'CREDIT_TYPE'
  | 'RECEIVING_TYPE'
  | 'SECCIONED_LINES'
  | 'AMOUNT_OF_DAYS'
  | 'USER'
  | 'DATE_TYPE'
  | 'DATE_TYPE_SALES_REPORT'
  | 'HISTORY'
  | 'VOUCHER'
  | 'BALANCE_TYPE'
  | 'FILENAME'
  | 'COLLECTION';

interface StateProps {
  filterOptions: Array<FilterTypes> | string[];
  handleSubmit: (data: any) => void;
  hideIcon?: boolean;
  singleLineFilter?: boolean;
  resetStartDate?: boolean;
}

type Props = StateProps;

const ChartFilterModal: React.FC<Props> = ({
  filterOptions,
  handleSubmit,
  hideIcon,
  singleLineFilter,
  resetStartDate,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const selectedStartDate = useSelector(
    (state: ApplicationState) => state.reports.selectedStartDate,
  );

  const selectedEndDate = useSelector(
    (state: ApplicationState) => state.reports.selectedEndDate,
  );

  const selectedEmployees = useSelector(
    (state: ApplicationState) => state.reports.selectedEmployees,
  );

  const selectedCars = useSelector(
    (state: ApplicationState) => state.reports.selectedCars,
  );

  const selectedProfiles = useSelector(
    (state: ApplicationState) => state.reports.selectedProfiles,
  );

  const selectedPayments = useSelector(
    (state: ApplicationState) => state.reports.selectedPayments,
  );

  const selectedQRCodes = useSelector(
    (state: ApplicationState) => state.reports.selectedQRCodes,
  );

  const selectedQuantity = useSelector(
    (state: ApplicationState) => state.reports.selectedQuantity,
  );

  const selectedLines = useSelector(
    (state: ApplicationState) => state.reports.selectedLines,
  );

  const startedAt = useSelector(
    (state: ApplicationState) => state.reports.startedAt,
  );

  const selectedReceivePayments = useSelector(
    (state: ApplicationState) => state.reports.selectedReceivePayments,
  );

  const selectedSupervisors = useSelector(
    (state: ApplicationState) => state.reports.selectedSupervisors,
  );

  const selectedDbds = useSelector(
    (state: ApplicationState) => state.reports.selectedDbds,
  );

  const openDialog = useSelector(
    (state: ApplicationState) => state.modalFilter.openModal,
  );

  const scorePercent = useSelector(
    (state: ApplicationState) => state.facial.scorePercent,
  );

  const selectedStatus = useSelector(
    (state: ApplicationState) => state.facial.selectedStatus,
  );

  const approvalStatus = useSelector(
    (state: ApplicationState) => state.facial.selectedApprovalStatus,
  );

  const tokenStatus = useSelector(
    (state: ApplicationState) => state.reports.selectedTokenStatus,
  );

  const creditType = useSelector(
    (state: ApplicationState) => state.reports.selectedCreditType,
  );

  const selectedType = useSelector(
    (state: ApplicationState) => state.companies.selectedType,
  );

  const selectedStatusReport = useSelector(
    (state: ApplicationState) => state.companies.selectedStatus,
  );

  const selectedPaymentType = useSelector(
    (state: ApplicationState) => state.companies.selectedPaymentType,
  );

  const selectedKmType = useSelector(
    (state: ApplicationState) => state.trips.kmType,
  );

  const selectedSellingPoint = useSelector(
    (state: ApplicationState) => state.reports.selectedSellingPoint,
  );

  const selectedSellingTerminal = useSelector(
    (state: ApplicationState) => state.reports.selectedSellingTerminal,
  );

  const selectedValidationStatus = useSelector(
    (state: ApplicationState) => state.facial.selectedValidationStatus,
  );

  const selectedReceivingType = useSelector(
    (state: ApplicationState) => state.sellingTerminals.selectedReceivingType,
  );

  const selectedSeccionedLine = useSelector(
    (state: ApplicationState) => state.reports.selectedSeccionedLine,
  );

  const selectedNumberOfDays = useSelector(
    (state: ApplicationState) => state.reports.numberOfDays,
  );

  const selectedSearchUser = useSelector(
    (state: ApplicationState) => state.users.selectedSearchUser,
  );

  const selectedDateType = useSelector(
    (state: ApplicationState) => state.reports.dateType,
  );

  const selectedHistory = useSelector(
    (state: ApplicationState) => state.reports.history,
  );

  const voucher = useSelector(
    (state: ApplicationState) => state.voucher.voucher,
  );

  const selectedBalanceType = useSelector(
    (state: ApplicationState) => state.balance.balanceType,
  );

  const filenameSelected = useSelector(
    (state: ApplicationState) => state.reports.filenameSelected,
  );

  const selectedCollection = useSelector(
    (state: ApplicationState) => state.employees.selectedCollection,
  );

  const [startDate, startOnChange] = useState<Date | null>(selectedStartDate);
  const [endDate, endOnChange] = useState<Date | null>(selectedEndDate);

  const [tab, setTab] = useState(String(filterOptions[0]));
  const [dayOrPeriod, setDayOrPeriod] = useState(true);
  const [isStartedAt, setIsStartedAt] = useState(tab === 'DAY');

  const handleTabs = (event: React.ChangeEvent<{}>, newTab: string) => {
    setTab(newTab);
    setDayOrPeriod(false);
    if (tab === 'PERIOD') {
      setDayOrPeriod(true);
    }
  };

  const handleDayOnChange = useCallback(
    (date: Date | null) => {
      dispatch(
        setSelectedStartedAt({
          startedAt: date!,
        }),
      );
    },
    [dispatch],
  );

  const handleStartOnChange = useCallback(
    (date: Date | null) => {
      startOnChange(date);
      dispatch(
        setSelectedStartDate({
          selectedStartDate: new Date(date!.setHours(0, 0, 0)),
        }),
      );
    },
    [dispatch],
  );

  const handleEndOnChange = useCallback(
    (date: Date | null) => {
      endOnChange(date);
      dispatch(
        setSelectedEndDate({
          selectedEndDate: new Date(date!.setHours(23, 59, 59)),
        }),
      );
    },
    [dispatch],
  );

  useEffect(
    () =>
      // unmounting
      () => {
        dispatch(
          setSelectedProfiles({
            selectedProfiles: [],
          }),
        );
        dispatch(
          setSelectedPayments({
            selectedPayments: [],
          }),
        );
        dispatch(
          setSelectedLines({
            selectedLines: [],
          }),
        );
        dispatch(
          setSelectedQRCodes({
            selectedQRCodes: [],
          }),
        );
        dispatch(
          setSelectedQuantity({
            selectedQuantity: 0,
          }),
        );
        dispatch(
          setSelectedEmployees({
            selectedEmployees: [],
          }),
        );
        dispatch(
          setSelectedReceivePayments({
            selectedReceivePayments: [],
          }),
        );
        dispatch(
          setSelectedSupervisors({
            selectedSupervisors: [],
          }),
        );
        dispatch(
          setSelectedDbds({
            selectedDbds: [],
          }),
        );
        dispatch(
          setSelectedTokenStatus({
            selectedTokenStatus: null,
          }),
        );
        dispatch(
          setSelectedCreditType({
            selectedCreditType: [],
          }),
        );
        dispatch(
          setSelectedScore({
            scorePercent: '',
          }),
        );
        dispatch(
          setSelectedStatus({
            selectedStatus: [],
          }),
        );
        dispatch(
          setSelectedApprovalStatus({
            selectedApprovalStatus: [],
          }),
        );
        dispatch(
          setSelectedType({
            selectedType: [],
          }),
        );
        dispatch(
          companySelectedStatus({
            selectedStatus: '',
          }),
        );
        dispatch(
          setSelectedPaymentType({
            selectedPaymentType: [],
          }),
        );
        dispatch(
          setSelectedSellingPoint({
            selectedSellingPoint: [],
          }),
        );
        dispatch(
          setSelectedSellingTerminal({
            selectedSellingTerminal: [],
          }),
        );
        dispatch(
          setSelectedValidationStatus({
            selectedValidationStatus: [],
          }),
        );
        dispatch(
          setSelectedSearchUser({
            selectedSearchUser: [],
          }),
        );
        dispatch(
          setSelectedReceivingType({
            selectedReceivingType: undefined,
          }),
        );
        dispatch(
          setToggleVoucher({
            voucher: null,
          }),
        );
      },
    [dispatch],
  );

  useEffect(() => {
    // Listening changes in dates for reset
    if (resetStartDate) {
      const date = new Date();
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);

      startOnChange(date);

      dispatch(
        dateSelectedStartDate({
          selectedStartDate: new Date(date),
        }),
      );
      return;
    }

    startOnChange(selectedStartDate);
    endOnChange(selectedEndDate);
    // eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    if (tab === 'DAY') setIsStartedAt(true);
    if (tab === 'PERIOD' && startDate?.getDate() !== endDate?.getDate()) {
      setIsStartedAt(false);
    }
  }, [tab, endDate, startDate]);

  const filterMap = new Map([
    [
      'DAY',
      {
        tab: (
          <Tab
            id='tab-day'
            aria-controls='scrollable-day'
            label='Dia'
            value='DAY'
            icon={<TodayIcon />}
          />
        ),
        tabPanel: (
          <SelectWeekDay
            value={startedAt}
            onChange={handleDayOnChange}
            variant='static'
          />
        ),
      },
    ],
    [
      'PAYMENT',
      {
        tab: (
          <Tab
            key='tab-payment'
            aria-controls='scrollable-payment'
            label='Forma de pagamento'
            value='PAYMENT'
            icon={<PaymentIcon />}
          />
        ),
        tabPanel: <PaymentFilter />,
      },
    ],
    [
      'QRCODE_TYPE',
      {
        tab: (
          <Tab
            key='tab-qrcode-type'
            aria-controls='scrollable-payment'
            label='Tipo do QRCode'
            value='QRCODE_TYPE'
            icon={<span className='material-icons'>qr_code</span>}
          />
        ),
        tabPanel: <QRCodeFilter />,
      },
    ],
    [
      'LINE_TYPE',
      {
        tab: (
          <Tab
            key='tab-line-type'
            aria-controls='scrollable-payment'
            label='Tipo da Linha'
            value='LINE_TYPE'
            icon={<span className='material-icons'>line</span>}
          />
        ),
        tabPanel: <QRCodeFilter />,
      },
    ],
    [
      'PROFILE',
      {
        tab: (
          <Tab
            key='tab-profile'
            aria-controls='scrollable-profile'
            label='Perfil'
            value='PROFILE'
            icon={<PersonPinIcon />}
          />
        ),
        tabPanel: <ProfileFilter />,
      },
    ],
    [
      'PERIOD',
      {
        tab: (
          <Tab
            key='tab-period'
            aria-controls='scrollable-profile'
            label='Período'
            value='PERIOD'
            icon={<DateRangeIcon />}
          />
        ),
        tabPanel: (
          <PeriodFilter
            startDate={startDate!}
            endDate={endDate!}
            startOnChange={handleStartOnChange}
            endOnChange={handleEndOnChange}
          />
        ),
      },
    ],
    [
      'USAGE',
      {
        tab: (
          <Tab
            key='tab-usage'
            aria-controls='scrollable-profile'
            label='Quantidade de uso'
            value='USAGE'
            icon={<PersonIcon />}
          />
        ),
        tabPanel: <UsageFilter />,
      },
    ],
    [
      'LINE',
      {
        tab: (
          <Tab
            key='tab-line'
            aria-controls='scrollable-line'
            label='Linhas'
            value='LINE'
            icon={<DirectionsBusIcon />}
          />
        ),
        tabPanel: <LineFilter singleLineFilter={singleLineFilter} />,
      },
    ],
    [
      'CAR',
      {
        tab: (
          <Tab
            key='tab-car'
            aria-controls='scrollable-line'
            label='Carros'
            value='CAR'
            icon={<Icon path={mdiBusMarker} size={1.1} />}
          />
        ),
        tabPanel: <CarsFilter />,
      },
    ],
    [
      'DRIVER',
      {
        tab: (
          <Tab
            key='tab-driver'
            aria-controls='scrollable-driver'
            label='Motorista'
            value='DRIVER'
            icon={<DriveEtaIcon />}
          />
        ),
        tabPanel: <DriverNavigation />,
      },
    ],
    [
      'RECEIVE_PAYMENT',
      {
        tab: (
          <Tab
            key='tab-receive-payment'
            aria-controls='scrollable-receive-payment'
            label='Forma de Recebimento'
            value='RECEIVE_PAYMENT'
            icon={<PaymentIcon />}
          />
        ),
        tabPanel: <ReceiveFilter />,
      },
    ],
    [
      'SUPERVISOR',
      {
        tab: (
          <Tab
            key='tab-supervisor'
            aria-controls='scrollable-supervisor'
            label='Supervisores'
            value='SUPERVISOR'
            icon={<SupervisorAccount />}
          />
        ),
        tabPanel: <SupervisorFilter />,
      },
    ],
    [
      'DBD',
      {
        tab: (
          <Tab
            key='tab-dbd'
            aria-controls='scrollable-dbd'
            label='DBDs'
            value='DBD'
            icon={<AdjustIcon />}
          />
        ),
        tabPanel: <DbdFilter />,
      },
    ],
    [
      'SCORE',
      {
        tab: (
          <Tab
            key='tab-score'
            aria-controls='scrollable-score'
            label='Score'
            value='SCORE'
            icon={<BeenhereTwoToneIcon />}
          />
        ),
        tabPanel: <ScoreFilter />,
      },
    ],
    [
      'STATUS',
      {
        tab: (
          <Tab
            key='tab-status'
            aria-controls='scrollable-status'
            label='Status'
            value='STATUS'
            icon={<ClassTwoTone />}
          />
        ),
        tabPanel: <StatusFilter />,
      },
    ],
    [
      'APPROVAL_STATUS',
      {
        tab: (
          <Tab
            key='tab-approval-status'
            aria-controls='scrollable-approval-status'
            label='Status de Aprovação'
            value='APPROVAL_STATUS'
            icon={<DoneAllTwoToneIcon />}
          />
        ),
        tabPanel: <ApprovalStatusFilter />,
      },
    ],
    [
      'TOKEN_STATUS',
      {
        tab: (
          <Tab
            key='tab-token-status'
            aria-controls='scrollable-token-status'
            label='Status do QR Code'
            value='TOKEN_STATUS'
            icon={<Icon path={mdiQrcode} size={1.1} />}
          />
        ),
        tabPanel: <TokenStatusFilter />,
      },
    ],
    [
      'KM_TYPE',
      {
        tab: (
          <Tab
            key='tab-km-type'
            aria-controls='scrollable-km-type'
            label='Tipo de Quilometragem'
            value='KM_TYPE'
            icon={<Commute />}
          />
        ),
        tabPanel: <KmTypeFilter />,
      },
    ],
    [
      'TRANSPORT_VOUCHER_TYPE',
      {
        tab: (
          <Tab
            key='tab-trasnport-voucher-type'
            aria-controls='scrollable-trasnport-voucher-type'
            label='Tipo'
            value='TRANSPORT_VOUCHER_TYPE'
            icon={<Commute />}
          />
        ),
        tabPanel: <TypeFilter />,
      },
    ],
    [
      'TRANSPORT_VOUCHER_STATUS',
      {
        tab: (
          <Tab
            key='tab-transport-voucher-status'
            aria-controls='scrollable-transport-voucher-status'
            label='Status'
            value='TRANSPORT_VOUCHER_STATUS'
            icon={<Autorenew />}
          />
        ),
        tabPanel: <TransportVoucherStatusFilter />,
      },
    ],
    [
      'TRANSPORT_VOUCHER_PAYMENT_TYPE',
      {
        tab: (
          <Tab
            key='tab-transport-voucher-payment-type'
            aria-controls='scrollable-transport-voucher-payment-type'
            label='Forma de Recebimento'
            value='TRANSPORT_VOUCHER_PAYMENT_TYPE'
            icon={<PaymentIcon />}
          />
        ),
        tabPanel: <PaymentTypeFilter />,
      },
    ],
    [
      'SELLING_POINT',
      {
        tab: (
          <Tab
            key='tab-transport-selling-point'
            aria-controls='scrollable-transport-selling-point'
            label='Ponto de venda'
            value='SELLING_POINT'
            icon={<HomeWorkIcon />}
          />
        ),
        tabPanel: <SellingPointFilter />,
      },
    ],
    [
      'SELLING_TERMINAL',
      {
        tab: (
          <Tab
            key='tab-transport-selling-terminal'
            aria-controls='scrollable-transport-selling-terminal'
            label='Rede de venda'
            value='SELLING_TERMINAL'
            icon={<HomeWorkIcon />}
          />
        ),
        tabPanel: <SellingTerminalFilter />,
      },
    ],

    [
      'REGISTRATION_VALIDATION_STATUS',
      {
        tab: (
          <Tab
            key='tab-registration-validation-status'
            aria-controls='scrollable-registration-validaiton-status'
            label='Status'
            value='REGISTRATION_VALIDATION_STATUS'
            icon={<ClassTwoTone />}
          />
        ),
        tabPanel: <ValidationStatusFilter />,
      },
    ],
    [
      'CREDIT_TYPE',
      {
        tab: (
          <Tab
            key='tab-credit-type'
            aria-controls='scrollable-token-status'
            label='Tipo de Crédito'
            value='CREDIT_TYPE'
            icon={<Receipt />}
          />
        ),
        tabPanel: <CreditTypeFilter />,
      },
    ],
    [
      'RECEIVING_TYPE',
      {
        tab: (
          <Tab
            key='tab-receiving-type'
            aria-controls='scrollable-receiving-type'
            label='Tipo de recebimento'
            value='RECEIVING_TYPE'
            icon={<Receipt />}
          />
        ),
        tabPanel: <ReceivingTypeFilter />,
      },
    ],
    [
      'SECCIONED_LINES',
      {
        tab: (
          <Tab
            key='tab-seccioned-lines'
            aria-controls='scrollable-token-status'
            label='Filtro por Trecho'
            value='SECCIONED_LINES'
            icon={<Receipt />}
          />
        ),
        tabPanel: <SeccionedLinesFilter />,
      },
    ],
    [
      'AMOUNT_OF_DAYS',
      {
        tab: (
          <Tab
            key='tab-amount-of-days'
            aria-controls='scrollable-status'
            label='Período'
            value='AMOUNT_OF_DAYS'
            icon={<DateRangeIcon />}
          />
        ),
        tabPanel: <AmountOfDaysFilter />,
      },
    ],
    [
      'USER',
      {
        tab: (
          <Tab
            key='tab-user-filter'
            aria-controls='scrollable-line'
            label='Usuários'
            value='USER'
            icon={<PersonIcon />}
          />
        ),
        tabPanel: <UserFilter />,
      },
    ],
    [
      'DATE_TYPE',
      {
        tab: (
          <Tab
            key='tab-date-type'
            aria-controls='scrollable-line'
            label='Tipo de Data'
            value='DATE_TYPE'
            icon={<DateRangeIcon />}
          />
        ),
        tabPanel: <DateTypeFilter />,
      },
    ],
    [
      'HISTORY',
      {
        tab: (
          <Tab
            key='tab-history'
            aria-controls='scrollable-line'
            label='Histórico'
            value='HISTORY'
            icon={<DateRangeIcon />}
          />
        ),
        tabPanel: <HistoryFilter />,
      },
    ],
    [
      'DATE_TYPE_SALES_REPORT',
      {
        tab: (
          <Tab
            key='tab-date-type'
            aria-controls='scrollable-line'
            label='Tipo de Data'
            value='DATE_TYPE_SALES_REPORT'
            icon={<DateRangeIcon />}
          />
        ),
        tabPanel: <DateTypeFilter salesReport />,
      },
    ],
    [
      'VOUCHER',
      {
        tab: (
          <Tab
            key='tab-voucher'
            aria-controls='scrollable-voucher'
            label='Voucher'
            value='VOUCHER'
            icon={<VoucherIcon />}
          />
        ),
        tabPanel: <VoucherFilter />,
      },
    ],
    [
      'BALANCE_TYPE',
      {
        tab: (
          <Tab
            key='tab-balance-type'
            aria-controls='scrollable-km-type'
            label='tipo de saldo'
            value='BALANCE_TYPE'
            icon={<AccountBalanceWalletIcon />}
          />
        ),
        tabPanel: <BalanceTypeFilter />,
      },
    ],
    [
      'FILENAME',
      {
        tab: (
          <Tab
            key='tab-filename-type'
            aria-controls='scrollable-filename-type'
            label='Nome do arquivo'
            value='FILENAME'
            icon={<AttachFileIcon />}
          />
        ),
        tabPanel: <FilenameFilter />,
      },
    ],
    [
      'COLLECTION',
      {
        tab: (
          <Tab
            key='tab-collection-type'
            aria-controls='scrollable-collection-type'
            label='Arrecadação'
            value='COLLECTION'
            icon={<WalletIcon />}
          />
        ),
        tabPanel: <CollectionFilter />,
      },
    ],
  ]);

  return (
    <>
      {!hideIcon && (
        <Tooltip title='Abre modal de filtros' placement='top'>
          <IconButton
            onClick={() => {
              dispatch(
                setOpenModal({
                  openModal: true,
                }),
              );
            }}
            classes={{
              root: classes.iconButton,
            }}>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
      <Dialog
        open={openDialog}
        aria-labelledby='dialog filter'
        onClose={() => {
          dispatch(
            setOpenModal({
              openModal: false,
            }),
          );
        }}>
        <DialogTitle id='dialog-filter'>Filtrar por:</DialogTitle>
        <DialogContent>
          <Tabs
            value={tab}
            onChange={handleTabs}
            scrollButtons='on'
            variant='scrollable'
            textColor='primary'
            indicatorColor='primary'
            aria-label='scrollable-dialog-filter'>
            {filterOptions.length > 0 &&
              filterOptions.map(
                (filter) => filterMap.has(filter) && filterMap.get(filter)!.tab,
              )}
          </Tabs>
          {/* Filter Navigations */}
          {filterOptions.length > 0 &&
            filterOptions.map(
              (filter) =>
                filterMap.has(filter) && (
                  <div role='tabpanel' key={filter}>
                    {filter === tab && (
                      <Box component='div' p={3}>
                        {filterMap.get(filter)!.tabPanel}
                      </Box>
                    )}
                  </div>
                ),
            )}
        </DialogContent>
        <DialogActions className={classes.buttons}>
          <Button
            color='primary'
            onClick={() => {
              dispatch(
                setOpenModal({
                  openModal: false,
                }),
              );
            }}>
            Cancelar
          </Button>
          <Button
            color='primary'
            variant='contained'
            onClick={() => {
              handleSubmit({
                isStartedAt,
                startDate,
                endDate,
                selectedCars,
                selectedProfiles,
                selectedPayments,
                selectedQRCodes,
                selectedQuantity,
                selectedLines,
                selectedEmployees,
                startedAt,
                selectedReceivePayments,
                selectedSupervisors,
                selectedDbds,
                scorePercent,
                selectedStatus,
                approvalStatus,
                tokenStatus,
                creditType,
                selectedType,
                selectedStatusReport,
                selectedPaymentType,
                selectedKmType,
                selectedSellingPoint,
                selectedSellingTerminal,
                selectedValidationStatus,
                dayOrPeriod,
                tab,
                selectedReceivingType,
                selectedSeccionedLine,
                selectedNumberOfDays,
                selectedSearchUser,
                selectedDateType,
                selectedHistory,
                voucher,
                selectedBalanceType,
                filenameSelected,
                selectedCollection,
              });
              dispatch(
                setOpenModal({
                  openModal: false,
                }),
              );
            }}>
            Filtrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export { ChartFilterModal };
export default ChartFilterModal;
